import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../../store/admin/vehiclesSocketStore";
import CommandExecuteModal from "./modals/CommandExecuteModal";
import AddCustomCommandModal from "./modals/AddCustomCommandModal";
import CommandsHistoryModal from "./modals/CommandsHistoryModal";
import CommandDetailModal from "./modals/CommandDetailModal";
import api from "../../../../../../utils/api";
import type { Command } from "../../../../../../store/reseller-admin/brandsStore";
import {
  useDeviceCommandsStore,
  getCommandStatusLabel,
  COMMAND_STATUS_COLORS,
  type SocketCommand,
} from "../../../../../../store/admin/deviceCommandsStore";

interface ActionsContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

type ModalType = "executeCommand" | "addCustomCommand" | "commandsHistory" | "commandDetail" | null;

// Role IDs with access to custom commands
const RESELLER_ROLE_ID = "Ne6pwRmFkr4bArJN47NZ";

// Only reseller can create/send custom commands
const CUSTOM_COMMAND_ALLOWED_ROLES = [RESELLER_ROLE_ID];

export default function ActionsContent({
  vehicle,
  isDarkMode,
}: ActionsContentProps) {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const [selectedSocketCommand, setSelectedSocketCommand] = useState<SocketCommand | null>(null);
  const [commands, setCommands] = useState<Command[]>([]);
  const [loadingCommands, setLoadingCommands] = useState(false);
  const [historyNoticeMessage, setHistoryNoticeMessage] = useState<string | null>(null);
  const { t } = useTranslation("lite");

  // Obtener comandos enviados desde el store
  const commandsByDevice = useDeviceCommandsStore((state) => state.commandsByDevice);

  // Memorizar los comandos del device actual
  const socketCommands = useMemo<SocketCommand[]>(() => {
    if (!vehicle?.id) return [];
    return commandsByDevice[vehicle.id] || [];
  }, [commandsByDevice, vehicle?.id]);

  // Check if user can access custom commands (carrier or reseller only)
  const userRoleId = localStorage.getItem("userRoleId");
  const canAccessCustomCommands = CUSTOM_COMMAND_ALLOWED_ROLES.includes(userRoleId || "");

  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";

  const brandId = vehicle.device?.brand_id;

  // Formatear timestamp a fecha/hora local
  const formatCommandTime = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const formatCommandDate = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Obtener color del estado del comando
  const getStatusColor = (status: SocketCommand["status"]) => {
    const colors = COMMAND_STATUS_COLORS[status];
    if (!colors) return { bg: "bg-gray-100", text: "text-gray-700" };
    return isDarkMode
      ? { bg: colors.darkBg, text: colors.darkText }
      : { bg: colors.bg, text: colors.text };
  };

  // Fetch commands when component mounts or brandId changes
  useEffect(() => {
    const fetchCommands = async () => {
      if (!brandId) {
        setCommands([]);
        return;
      }

      setLoadingCommands(true);
      try {
        const response = await api.get(`/commandsList?brand_id=${brandId}&limit=50`);
        const data = response.data?.data || [];

        // Filter commands by user role if roles are specified
        const userRoleId = localStorage.getItem("userRoleId");
        const filteredCommands = data.filter((cmd: Command) => {
          // If command has no roles specified, it's visible to everyone
          if (!cmd.roles || cmd.roles.length === 0) return true;
          // Reseller users can see all commands
          if (userRoleId === RESELLER_ROLE_ID) return true;
          // For other roles, check if user's role is in the command's roles list
          return cmd.roles.includes(userRoleId || "");
        });

        setCommands(filteredCommands);
      } catch (error) {
        console.error("Error fetching commands:", error);
        setCommands([]);
      } finally {
        setLoadingCommands(false);
      }
    };

    fetchCommands();
  }, [brandId]);

  const handleOpenModal = (modalType: ModalType) => {
    setActiveModal(modalType);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setSelectedCommand(null);
    setSelectedSocketCommand(null);
    setHistoryNoticeMessage(null);
  };

  const handleSendCommandClientTimeout = () => {
    setSelectedCommand(null);
    setSelectedSocketCommand(null);
    setHistoryNoticeMessage(t("deviceCommands.clientHttpTimeoutNotice"));
    setActiveModal("commandsHistory");
  };

  const handleSocketCommandClick = (command: SocketCommand) => {
    setSelectedSocketCommand(command);
    setActiveModal("commandDetail");
  };

  const handleOpenHistory = () => {
    setActiveModal("commandsHistory");
  };

  const handleCommandClick = (command: Command) => {
    setSelectedCommand(command);
    setActiveModal("executeCommand");
  };

  const handleCommandSent = () => {
    // Refresh commands list or show success message
    handleCloseModal();
  };

  const handleCustomCommandCreated = () => {
    // Refresh commands list
    if (brandId) {
      setLoadingCommands(true);
      api.get(`/commandsList?brand_id=${brandId}&limit=50`)
        .then((response) => {
          const data = response.data?.data || [];
          const userRoleId = localStorage.getItem("userRoleId");
          const filteredCommands = data.filter((cmd: Command) => {
            // If command has no roles specified, it's visible to everyone
            if (!cmd.roles || cmd.roles.length === 0) return true;
            // Reseller users can see all commands
            if (userRoleId === RESELLER_ROLE_ID) return true;
            // For other roles, check if user's role is in the command's roles list
            return cmd.roles.includes(userRoleId || "");
          });
          setCommands(filteredCommands);
        })
        .catch(console.error)
        .finally(() => setLoadingCommands(false));
    }
    handleCloseModal();
  };

  const renderModal = () => {
    switch (activeModal) {
      case "executeCommand":
        return selectedCommand ? (
          <CommandExecuteModal
            vehicle={vehicle}
            command={selectedCommand}
            isDarkMode={isDarkMode}
            onClose={handleCloseModal}
            onSuccess={handleCommandSent}
            onSendCommandClientTimeout={handleSendCommandClientTimeout}
          />
        ) : null;
      case "addCustomCommand":
        return (
          <AddCustomCommandModal
            vehicle={vehicle}
            isDarkMode={isDarkMode}
            onClose={handleCloseModal}
            onSuccess={handleCustomCommandCreated}
            onSendCommandClientTimeout={handleSendCommandClientTimeout}
          />
        );
      case "commandsHistory":
        return (
          <CommandsHistoryModal
            vehicle={vehicle}
            isDarkMode={isDarkMode}
            onClose={handleCloseModal}
            onCommandClick={handleSocketCommandClick}
            noticeMessage={historyNoticeMessage}
          />
        );
      case "commandDetail":
        return selectedSocketCommand ? (
          <CommandDetailModal
            command={selectedSocketCommand}
            isDarkMode={isDarkMode}
            onClose={handleCloseModal}
          />
        ) : null;
      default:
        return null;
    }
  };

  const generalCommands = commands.filter((cmd) => !cmd.company_id);
  const customCommands = commands.filter((cmd) => cmd.company_id);

  return (
    <>
      <div className={`p-3 border-t ${borderColor}`}>
        <h4 className={`font-semibold text-sm mb-3 ${textColor}`}>
          {t("actionsContent.title")}
        </h4>

        {/* Historial de comandos enviados */}
        <div className="mb-4">
          <p className={`text-xs ${textSecondary} mb-2`}>
            {t("deviceCommands.commandHistory")}
          </p>

          {/* Header de la tabla */}
          <div
            className={`grid grid-cols-12 gap-2 text-[10px] font-medium ${textSecondary} mb-2 pb-2 border-b ${borderColor}`}
          >
            <div className="col-span-4">{t("deviceCommands.command")}</div>
            <div className="col-span-4">{t("deviceCommands.dateTime")}</div>
            <div className="col-span-4">{t("deviceCommands.status")}</div>
          </div>

          {/* Empty state - cuando no hay comandos */}
          {socketCommands.length === 0 && (
            <div className="text-center py-4">
              <p className={`text-xs ${textSecondary}`}>
                {t("deviceCommands.waitingCommands")}
              </p>
            </div>
          )}

          {/* Lista de comandos (solo 3) */}
          {socketCommands.length > 0 && (
            <div className="space-y-2 mb-3">
              {socketCommands.slice(0, 3).map((cmd) => {
                const statusColor = getStatusColor(cmd.status);
                return (
                  <div
                    key={cmd.id}
                    onClick={() => handleSocketCommandClick(cmd)}
                    className={`grid grid-cols-12 gap-2 text-[10px] items-center py-2 border-b ${borderColor} hover:border-primary-500 transition-colors`}
                  >
                    <div className={`col-span-4 ${textColor} font-medium truncate`}>
                      {cmd.command_name || cmd.command_code}
                    </div>
                    <div className={`col-span-4 ${textSecondary}`}>
                      <div>{formatCommandDate(cmd.receivedAt)}</div>
                      <div>{formatCommandTime(cmd.receivedAt)}</div>
                    </div>
                    <div className="col-span-4">
                      <span
                        className={`text-[9px] px-1.5 py-0.5 rounded ${statusColor.bg} ${statusColor.text}`}
                      >
                        {getCommandStatusLabel(cmd.status)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Botón Ver más */}
          <button
            onClick={handleOpenHistory}
            className={`w-full py-2 rounded-lg border ${borderColor} ${textColor} text-xs font-medium hover:border-primary-500 hover:text-primary-500 transition-colors flex items-center justify-center gap-1`}
          >
            {t("deviceCommands.viewMore")} <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>

        {/* Loading state */}
        {loadingCommands && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
          </div>
        )}

        {/* No brand_id message */}
        {!brandId && !loadingCommands && (
          <div className={`text-center py-4 ${textSecondary}`}>
            <i className="ri-error-warning-line text-2xl mb-2 block opacity-50"></i>
            <p className="text-xs">{t("deviceCommands.noBrand")}</p>
          </div>
        )}

        {/* Commands buttons */}
        {brandId && !loadingCommands && (
          <>
            {/* General commands from brand */}
            {generalCommands.length > 0 && (
              <div className="mb-4">
                <p className={`text-xs ${textSecondary} mb-2`}>
                  {t("deviceCommands.generalCommands")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {generalCommands.map((command) => (
                    <button
                      key={command.id}
                      onClick={() => handleCommandClick(command)}
                      className={`px-3 py-1.5 text-xs rounded-lg border border-primary-500 hover:bg-primary-500 hover:text-black transition-colors ${textColor} flex items-center gap-1`}
                    >
                      {command.password_required && (
                        <i className="ri-lock-line text-yellow-500 text-[10px]"></i>
                      )}
                      {command.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom commands for this company */}
            {customCommands.length > 0 && (
              <div className="mb-4">
                <p className={`text-xs ${textSecondary} mb-2`}>
                  {t("deviceCommands.customCommands")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {customCommands.map((command) => (
                    <button
                      key={command.id}
                      onClick={() => handleCommandClick(command)}
                      className={`px-3 py-1.5 text-xs rounded-lg border border-blue-500 hover:bg-blue-500 hover:text-white transition-colors ${textColor} flex items-center gap-1`}
                    >
                      {command.password_required && (
                        <i className="ri-lock-line text-yellow-500 text-[10px]"></i>
                      )}
                      {command.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No commands message */}
            {commands.length === 0 && (
              <div className={`text-center py-4 ${textSecondary}`}>
                <i className="ri-terminal-box-line text-2xl mb-2 block opacity-50"></i>
                <p className="text-xs">{t("deviceCommands.noCommands")}</p>
              </div>
            )}

            {/* Add custom command button - Only for carrier and reseller roles */}

            {canAccessCustomCommands && (
              <button
                onClick={() => handleOpenModal("addCustomCommand")}
                className={`w-full mt-2 px-3 py-2 text-xs rounded-lg border ${borderColor} ${textColor} hover:border-primary-500 hover:text-primary-500 transition-colors flex items-center justify-center gap-2`}
              >
                <i className="ri-terminal-line"></i>
                {t("deviceCommands.sendCustomCommand")}
              </button>
            )} 
          </>
        )}
      </div>

      {/* Modals */}
      {activeModal && renderModal()}
    </>
  );
}
